import { HubFeature } from '@ats/api/cm';
import { LocDef, LocDefs, LocMap, MapLocKeys } from '@cm/core/localization';

const dpRoot = 'Catalogs|Achievements|eCertificate|';

const sharedData: Pick<LocDef, 'htmlAllowed' | 'shared' | 'portal'> = {
	htmlAllowed: false,
	shared: false,
	portal: 'SP',
};

export type SPAchievementEcertLocs =
	| 'SPAchievementEcertSectionTitle'
	| 'SPAchievementEcertEdit'
	| 'SPAchievementEcertUpdate'
	| 'SPAchievementEcertCancel'
	| 'SPAchievementEcertEnabled'
	| 'SPAchievementEcertTemplate'
	| 'SPAchievementEcertFileHint'
	| 'SPAchievementEcertFileSizeError'
	| 'SPAchievementEcertFileTypeError'
	| 'SPAchievementEcertCheckboxHint'
	| 'SPAchievementEcertTabTitle'
	| 'SPAchievementEcertHeading';

export const spAchievementEcertLocs: LocDefs<SPAchievementEcertLocs> = {
	SPAchievementEcertHeading: {
		defaultValue: 'Achievements Catalog',
		description: 'Page heading for the Achievement Ecertificate page',
		legacyName: 'Credential.Achievements.Certificate/CatalogsAchievement.Ecert.Title.TabTitle',
		displayPath: `${dpRoot}Tab Title`,
		displayType: 'Title',
		feature: HubFeature.CredentialAchievementsCertificate,
		...sharedData,
	},
	SPAchievementEcertTabTitle: {
		defaultValue: 'Achievement ECertificate',
		description: 'Tab title for the Achievement Ecertificate page.',
		legacyName: 'Credential.Achievements.Certificate/CatalogsAchievement.Ecert.Title.Heading',
		displayPath: `${dpRoot}Heading`,
		displayType: 'Title',
		feature: HubFeature.CredentialAchievementsCertificate,
		...sharedData,
	},
	SPAchievementEcertSectionTitle: {
		defaultValue: 'ECertificate',
		description: 'Page section title.',
		legacyName:
			'Credential.Achievements.Certificate/CatalogsAchievement.Ecert.Title.SectionTitle',
		displayPath: `${dpRoot}Page Section Title`,
		displayType: 'Title',
		feature: HubFeature.CredentialAchievementsCertificate,
		...sharedData,
	},
	SPAchievementEcertEdit: {
		defaultValue: 'Edit',
		description: 'Button to edit the eCertificate.',
		legacyName: 'Credential.Achievements.Certificate/CatalogsAchievement.Ecert.Button.Edit',
		displayPath: `${dpRoot}Edit`,
		displayType: 'Button',
		feature: HubFeature.CredentialAchievementsCertificate,
		...sharedData,
	},
	SPAchievementEcertUpdate: {
		defaultValue: 'Update',
		description: 'Button to update the eCertificate.',
		legacyName: 'Credential.Achievements.Certificate/CatalogsAchievement.Ecert.Button.Update',
		displayPath: `${dpRoot}Update`,
		displayType: 'Button',
		feature: HubFeature.CredentialAchievementsCertificate,
		...sharedData,
	},
	SPAchievementEcertCancel: {
		defaultValue: 'Cancel',
		description: 'Button to cancel editing the eCertificate.',
		legacyName: 'Credential.Achievements.Certificate/CatalogsAchievement.Ecert.Button.Cancel',
		displayPath: `${dpRoot}Cancel`,
		displayType: 'Button',
		feature: HubFeature.CredentialAchievementsCertificate,
		...sharedData,
	},
	SPAchievementEcertEnabled: {
		defaultValue: 'Certificate Download Enabled',
		description: 'Label display for field Certificate Download Enabled.',
		legacyName:
			'Credential.Achievements.Certificate/CatalogsAchievement.Ecert.FieldLabel.Enabled',
		displayPath: `${dpRoot}Enabled`,
		displayType: 'Button',
		feature: HubFeature.CredentialAchievementsCertificate,
		...sharedData,
	},
	SPAchievementEcertTemplate: {
		defaultValue: 'ECertificate Template',
		description: 'Label for ECertificate template.',
		legacyName: 'Credential.Achievements.Certificate/CatalogsAchievement.Ecert.Label.File',
		displayPath: `${dpRoot}Ecetificate Template`,
		displayType: 'Label',
		feature: HubFeature.CredentialAchievementsCertificate,
		...sharedData,
	},
	SPAchievementEcertFileHint: {
		defaultValue: 'The file must be a .docx format and not exceed 5mb.',
		description: 'Hint for ECertificate template file.',
		legacyName: 'Credential.Achievements.Certificate/CatalogsAchievement.Ecert.Hint.File',
		displayPath: `${dpRoot}Ecetificate File Hint`,
		displayType: 'Content',
		feature: HubFeature.CredentialAchievementsCertificate,
		...sharedData,
	},
	SPAchievementEcertFileSizeError: {
		defaultValue: 'The file must not exceed 5 MB.',
		description: 'Error displayed when file is larger than 5 MB.',
		legacyName: 'Credential.Achievements.Certificate/CatalogsAchievement.Ecert.FieldError.Size',
		displayPath: `${dpRoot}Ecetificate File Size Error`,
		displayType: 'Error',
		feature: HubFeature.CredentialAchievementsCertificate,
		...sharedData,
	},
	SPAchievementEcertFileTypeError: {
		defaultValue: 'The file must be a .docx format',
		description: 'Error displayed when file type is incorrect.',
		legacyName: 'Credential.Achievements.Certificate/CatalogsAchievement.Ecert.FieldError.Type',
		displayPath: `${dpRoot}Ecetificate File Type Error`,
		displayType: 'Error',
		feature: HubFeature.CredentialAchievementsCertificate,
		...sharedData,
	},
	SPAchievementEcertCheckboxHint: {
		defaultValue: 'A file must be attached or uploaded before enabling download.',
		description: 'Hint for the checkbox to enable the ECertificate Download.',
		legacyName:
			'Credential.Achievements.Certificate/CatalogsAchievement.Ecert.FieldHint.Enabled',
		displayPath: `${dpRoot}Enabled Hint`,
		displayType: 'Content',
		feature: HubFeature.CredentialAchievementsCertificate,
		...sharedData,
	},
};

export type SPAchievementEcertLocKeys = LocMap<SPAchievementEcertLocs>;

export const spAchievementEcertLocKeys: SPAchievementEcertLocKeys =
	MapLocKeys<SPAchievementEcertLocs>(spAchievementEcertLocs);
